import { useFeatureFlag } from '@nx/state';
import { isNotNullish } from '@nx/stdlib';
import { useMemo } from 'react';

import { useDispatch } from '../../context';
import relateApi, { useListDbmssQuery } from './relate.api';
import { getRelateErrors } from './relate.error';

export function useDbmsList(dbmsIds?: string[], options: { polling: boolean } = { polling: false }) {
  const [isPollingDisabled] = useFeatureFlag('local:disable-polling');
  const {
    data: dbmssAdapter,
    isLoading,
    isError,
    error,
  } = useListDbmssQuery(
    dbmsIds,
    // Removing the polling should get rid of conflicts with the DBMS
    // status watcher (subscription) adding it behind feature flag as I'm not
    // sure about the watcher's performance in all OSes.
    options.polling && !isPollingDisabled ? { pollingInterval: 5000, skipPollingIfUnfocused: true } : {},
  );

  const dbmss = useMemo(
    () => dbmssAdapter?.ids.map((id) => dbmssAdapter.entities[id]).filter((e) => isNotNullish(e)) ?? [],
    [dbmssAdapter],
  );

  return {
    isLoading,
    isError,
    dbmss,
    error: getRelateErrors(error),
  };
}

/** Meant to be used when stopping multiple DBMSs through the same hook */
export function useStopDbms() {
  const dispatch = useDispatch();

  return (dbmsId: string) =>
    dispatch(relateApi.endpoints.stopDbms.initiate(dbmsId, { fixedCacheKey: `dbms-stop-${dbmsId}` })).unwrap();
}

/** Meant to be used when starting multiple DBMSs through the same hook */
export function useStartDbms() {
  const dispatch = useDispatch();

  return (dbmsId: string) =>
    dispatch(relateApi.endpoints.startDbms.initiate(dbmsId, { fixedCacheKey: `dbms-start-${dbmsId}` })).unwrap();
}

/** Meant to be used when restarting multiple DBMSs through the same hook */
export function useRestartDbms() {
  const stopDbms = useStopDbms();
  const startDbms = useStartDbms();

  return async (dbmsId: string) => {
    await stopDbms(dbmsId);
    await startDbms(dbmsId);
  };
}
